
import React, { useState } from 'react';
import { ModelConfig, ServiceStatus, ProviderCategory } from '../types';
import { backendService } from '../services/backendService';

interface ModelManagerProps {
  models: ModelConfig[];
  setModels: React.Dispatch<React.SetStateAction<ModelConfig[]>>;
}

const ModelManager: React.FC<ModelManagerProps> = ({ models, setModels }) => {
  const [activeCategory, setActiveCategory] = useState<ProviderCategory>('LLM');
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<Partial<ModelConfig> | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [availableDiscovery, setAvailableDiscovery] = useState<string[]>([]);

  const handleTestConnection = async () => {
    if (!editingModel?.endpoint) return;
    setTesting(true);
    const result = await backendService.testEndpoint(editingModel.endpoint);
    setTestResult(result);
    setTesting(false);
    setTimeout(() => setTestResult(null), 4000);
  };

  const handleFetchModels = async () => {
    if (!editingModel?.endpoint) return;
    setTesting(true);
    try {
      const modelsList = await backendService.fetchProviderModels(editingModel.endpoint);
      setAvailableDiscovery(modelsList);
      if (modelsList.length > 0 && !editingModel.name) {
        setEditingModel(prev => ({ ...prev!, name: modelsList[0] }));
      }
      setTestResult({ success: true, message: `Found ${modelsList.length} models` });
    } catch (e: any) {
      setTestResult({ success: false, message: "Discovery Failed" });
    } finally {
      setTesting(false);
      setTimeout(() => setTestResult(null), 4000);
    }
  };

  const getStatusConfig = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.ONLINE: return { color: 'bg-green-500', text: 'ONLINE', badge: 'bg-green-500/10 text-green-400 border-green-500/20' };
      case ServiceStatus.DEGRADED: return { color: 'bg-orange-500', text: 'WARM', badge: 'bg-orange-500/10 text-orange-400 border-orange-500/20' };
      case ServiceStatus.OFFLINE: return { color: 'bg-red-500', text: 'OFFLINE', badge: 'bg-red-500/10 text-red-400 border-red-500/20' };
      default: return { color: 'bg-slate-500', text: 'UNKNOWN', badge: 'bg-slate-500/10 text-slate-400 border-slate-500/20' };
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingModel) return;

    if (editingModel.id) {
      const success = await backendService.updateModel(editingModel.id, editingModel);
      if (success) {
        setModels(prev => prev.map(m => m.id === editingModel.id ? { ...m, ...editingModel } as ModelConfig : m));
      }
    } else {
      const newModel = await backendService.addModel({ 
        ...editingModel, 
        status: ServiceStatus.ONLINE,
        parameters: editingModel.parameters || { temperature: 0.7, topP: 0.9 }
      });
      if (newModel) setModels(prev => [...prev, newModel]);
    }
    setShowModal(false);
    setEditingModel(null);
    setAvailableDiscovery([]);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Destroy this node configuration?')) return;
    const success = await backendService.deleteModel(id);
    if (success) setModels(prev => prev.filter(m => m.id !== id));
  };

  const filteredModels = models.filter(m => m.category === activeCategory);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">Neural Fleet <span className="text-purple-500">Inventory</span></h2>
          <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-0.5">Local Provider Registry</p>
        </div>
        <button 
          onClick={() => { 
            setEditingModel({ category: activeCategory, providerType: 'OLLAMA', endpoint: 'http://localhost:11434' }); 
            setAvailableDiscovery([]);
            setShowModal(true); 
          }}
          className="bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded font-bold text-[10px] uppercase tracking-wider transition-all shadow-lg shadow-purple-900/20"
        >
          + Deploy Provider
        </button>
      </div>

      <div className="flex gap-1 p-1 bg-[#131722] border border-[#2a2e39] rounded w-fit">
        {['LLM', 'VLM', 'ASR', 'TTS'].map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat as ProviderCategory)}
            className={`px-3 py-1 rounded text-[10px] font-black uppercase tracking-widest transition-all ${
              activeCategory === cat ? 'bg-purple-600 text-white' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredModels.map(model => {
          const status = getStatusConfig(model.status);
          return (
            <div key={model.id} className="bg-[#1e222d] rounded border border-[#2a2e39] flex flex-col hover:border-purple-500/40 transition-all group overflow-hidden">
              <div className="p-4 border-b border-[#2a2e39] bg-[#1c202a]">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${status.color} shadow-[0_0_8px] shadow-current`}></div>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${status.badge}`}>
                      {status.text}
                    </span>
                  </div>
                  <span className="text-[9px] font-mono font-bold text-slate-600 uppercase">id: {model.id}</span>
                </div>
                <h4 className="text-base font-black text-white tracking-tight truncate">{model.name}</h4>
                <div className="flex items-center gap-2 mt-1">
                   <span className="text-[9px] font-black text-purple-400 bg-purple-400/10 px-1 rounded">{model.providerType}</span>
                   <p className="text-[9px] font-mono text-slate-500 truncate">{model.endpoint}</p>
                </div>
              </div>

              <div className="p-4 flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#131722] p-2 rounded border border-[#2a2e39]">
                    <p className="text-[8px] font-bold text-slate-600 uppercase mb-0.5">Context</p>
                    <p className="text-xs font-mono text-slate-200">{model.contextLength > 0 ? `${(model.contextLength/1024).toFixed(0)}K` : '∞'}</p>
                  </div>
                  <div className="bg-[#131722] p-2 rounded border border-[#2a2e39]">
                    <p className="text-[8px] font-bold text-slate-600 uppercase mb-0.5">Temp</p>
                    <p className="text-xs font-mono text-slate-200">{model.parameters?.temperature || 0.7}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-[#131722]/50 border-t border-[#2a2e39] flex gap-2">
                <button 
                  onClick={() => { setEditingModel(model); setShowModal(true); }}
                  className="flex-1 py-1.5 bg-[#2a2e39] hover:bg-[#363a45] text-slate-300 rounded text-[10px] font-black uppercase tracking-wider transition-all"
                >
                  Configure
                </button>
                <button 
                  onClick={() => handleDelete(model.id)}
                  className="w-8 py-1.5 bg-red-900/10 hover:bg-red-500/20 text-red-500 rounded border border-red-500/20 transition-all"
                >
                  <i className="fas fa-trash-alt text-xs"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <form onSubmit={handleSave} className="bg-[#1e222d] border border-[#2a2e39] rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-[#2a2e39] bg-[#131722]/60 flex justify-between items-center">
              <h3 className="text-[10px] font-black text-white uppercase tracking-widest">{editingModel?.id ? 'Modify' : 'Initialize'} Node</h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white"><i className="fas fa-times"></i></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <label className="text-[9px] font-bold text-slate-500 uppercase">Endpoint URL</label>
                  {testResult && (
                    <span className={`text-[8px] font-black uppercase ${testResult.success ? 'text-green-500' : 'text-red-500'}`}>
                      {testResult.message}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input 
                    required 
                    value={editingModel?.endpoint || ''} 
                    onChange={e => setEditingModel(p => ({...p!, endpoint: e.target.value}))} 
                    placeholder="http://localhost:11434" 
                    className="flex-1 bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-xs text-white focus:border-purple-500 outline-none font-mono" 
                  />
                  <button type="button" onClick={handleFetchModels} disabled={testing} className="px-3 bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white border border-purple-500/30 rounded text-[9px] font-black uppercase tracking-widest transition-all">
                    {testing ? '...' : 'Discover'}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-500 uppercase">Provider Model / Name</label>
                <div className="relative">
                  <input 
                    list="discovered-models"
                    required 
                    value={editingModel?.name || ''} 
                    onChange={e => setEditingModel(p => ({...p!, name: e.target.value}))} 
                    className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-xs text-white focus:border-purple-500 outline-none font-mono" 
                    placeholder="e.g. llama3.1:8b"
                  />
                  <datalist id="discovered-models">
                    {availableDiscovery.map(m => <option key={m} value={m} />)}
                  </datalist>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-500 uppercase">Category</label>
                  <select value={editingModel?.category || 'LLM'} onChange={e => setEditingModel(p => ({...p!, category: e.target.value as any}))} className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-xs text-white focus:border-purple-500 outline-none">
                    <option>LLM</option><option>VLM</option><option>ASR</option><option>TTS</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-500 uppercase">Stack Type</label>
                  <select value={editingModel?.providerType || 'OLLAMA'} onChange={e => setEditingModel(p => ({...p!, providerType: e.target.value as any}))} className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-xs text-white focus:border-purple-500 outline-none">
                    <option>OLLAMA</option><option>LOCAL_V1</option><option>CUSTOM_POST</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#131722]/40 border-t border-[#2a2e39] flex gap-3">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 bg-[#2a2e39] text-slate-400 rounded text-[10px] font-black uppercase hover:text-white transition-all">Cancel</button>
              <button type="submit" className="flex-1 py-2 bg-purple-600 text-white rounded text-[10px] font-black uppercase shadow-lg shadow-purple-900/20 hover:bg-purple-500 transition-all">Save Config</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ModelManager;
